import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userService, Category } from '../services/userService';

interface CategoriesContextType {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  updateCategories: (newCategories: Category[]) => Promise<void>;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

interface CategoriesProviderProps {
  children: ReactNode;
}

export const CategoriesProvider: React.FC<CategoriesProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCategories = async () => {
    try {
      setError(null);
      const userCategories = await userService.getCategories();
      setCategories(userCategories);

      // Sync with localStorage for backwards compatibility
      localStorage.setItem('userCategories', JSON.stringify(userCategories));
    } catch (err: any) {
      console.error('Failed to load categories:', err);
      setError(err.message);

      // Fallback to localStorage if backend fails
      const savedCategories = localStorage.getItem('userCategories');
      if (savedCategories) {
        try {
          setCategories(JSON.parse(savedCategories));
        } catch (parseErr) {
          console.error('Failed to parse saved categories:', parseErr);
        }
      }
    }
  };

  const updateCategories = async (newCategories: Category[]) => {
    try {
      setError(null);
      const updatedCategories = await userService.updateCategories(newCategories);
      setCategories(updatedCategories);

      // Sync with localStorage
      localStorage.setItem('userCategories', JSON.stringify(updatedCategories));
    } catch (err: any) {
      console.error('Failed to update categories:', err);
      setError(err.message);

      // Still update local state as fallback
      setCategories(newCategories);
      localStorage.setItem('userCategories', JSON.stringify(newCategories));
      throw err; // Re-throw so calling component can handle
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      await refreshCategories();
      setLoading(false);
    };

    loadCategories();
  }, []);

  const value: CategoriesContextType = {
    categories,
    loading,
    error,
    refreshCategories,
    updateCategories,
  };

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = (): CategoriesContextType => {
  const context = useContext(CategoriesContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};